<template>
  <form v-if="locations">
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>Locations</h1>

        <a @click="$router.go(-1)" class="btn btn-info" active-class="">
          <i class="fa fa-chevron-left"></i> Back
        </a>
      </div>
    </div>

    <div class="container-fluid">
      <div class="dashboard-block">
        <div class="row">
          <div class="col-md-10 col-lg-8">
            <h2>Based In</h2>
            <selectize
              ref="based_in"
              class="selectize selectize-single"
              name="based_in"
              :groups="locationGroups"
              :options="locationOptions"
              v-model="stateBasedIn"
              @input="getFilteredData"/>
            <div v-if="this.stateLocationRegionBasedIn">
              <h3>Select a region</h3>
              <selectize
                ref="region_based_in"
                class="selectize selectize-single"
                name="region_based_in"
                key-context="region_based_in"
                v-model="stateLocationRegionBasedIn"
                :fetch-results="handleGetLocationRegionsCallback"
                :fetch-more-results="handleGetNextLocationRegionsCallback"
                :options="basedInRegionOptions"/>
            </div>
          </div>
          <div class="decorative-divider"></div>
          <div class="col-md-10 col-lg-8">
            <h2>Available In</h2>
            <ul class="large-checklist">
              <li v-for="location in locations" :key="location.id">
                <div class="checkbox checkbox-primary">
                  <input type="checkbox" :id="`checkbox-available-in-${location.id}`" :value="{ id: location.id }" v-model="stateLocations" @input="$event => updateStateLocations( location, $event.target.checked )">
                  <label :for="`checkbox-available-in-${location.id}`">
                    {{ location.name }}
                  </label>
                  <ul v-if="location.children && location.children.length > 0">
                    <li v-for="childLocation in location.children" :key="childLocation.id">
                      <input type="checkbox" :id="`checkbox-available-in-${childLocation.id}`" :value="{ id: childLocation.id }" v-model="stateLocations" @input="$event => updateStateLocations( childLocation, $event.target.checked )">
                      <label :for="`checkbox-available-in-${childLocation.id}`">
                        {{ childLocation.name }}
                      </label>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>

            <ladda-button
              type="submit"
              class="btn btn-sm btn-primary"
              :click="handleSaveLocationsCallback">
              Save Changes
            </ladda-button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { stateMapper } from 'mixins/users'
import { notify } from 'utils'
import Selectize from '../ext/codium/CodiumSelectize'
import {map, pick} from 'lodash'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [stateMapper, OpportunityTracker],

  components: {
    Selectize
  },

  data () {
    return {
      locationOptions: [],
      locationGroups: [],
      basedInRegionOptions: [],
      locationSuburbsMeta: {}
    }
  },

  computed: {
    ...mapState('location', ['locations', 'location_regions'])
  },

  created () {
    this.getLocations().then((response) => {
      const parentLocations = response.data
      let allLocations = []
      parentLocations.forEach(location => {
        allLocations = allLocations.concat(location.children)
      })
      this.locationGroups = parentLocations.map(l => ({ id: l.id, text: l.name }))
      this.locationOptions = map(allLocations, location => ({ id: location.id, text: location.name, location_regions: location.location_regions, group: location.parent_location_id }))
    })

    this.getLocationRegions({ id: this.stateBasedIn, params: {} }).then((response) => {
      this.locationSuburbsMeta = pick(response.data.meta, ['current_page', 'per_page', 'total', 'last_page'])
      if (this.basedInRegionOptions.length > 0) {
        this.basedInRegionOptions = []
      }
      this.pushRegionLocationItems(response, [])
    }).catch(() => {
    })
  },

  methods: {
    ...mapActions('location', ['getLocations', 'getLocationRegions', 'getNextLocationRegions']),
    ...mapActions('talent/location', ['updateTalentLocations']),

    handleSaveLocationsCallback () {
      return this.updateTalentLocations({
        id: this.user.id,
        payload: {
          locations: this.user.locations,
          basedIn: this.user.based_in.id,
          locationRegion: this.user.location_region.id
        }
      }).then(() => {
        notify.success('Successfully saved locations you\'re based in.')
        this.trackEvent(this.analytics.V2.AccountSectionEditEvent, { sectionName: this.analytics.V2.ValidatedAccountSectionName.LOCATIONS })
      }).catch((errors) => {
        notify.error('Sorry something has gone wrong, please try again.')
      })
    },

    updateStateLocations (location, isChecked) {
      if (location.parent_location_id && isChecked) {
        if (!this.stateLocations.some(l => l.id === location.parent_location_id)) {
          this.stateLocations.push({ id: location.parent_location_id })
        }
      }

      if (location.children && !isChecked) {
        const childIds = location.children.map(child => child.id)
        this.stateLocations = this.stateLocations.filter(l => !childIds.includes(l.id))
      }
    },

    pushRegionLocationItems (response, initialOptions) {
      let newRegionLocationOptions = initialOptions
      response.data.data.forEach(region => newRegionLocationOptions.push({
        id: region.id,
        text: region.name
      }))
      this.basedInRegionOptions = newRegionLocationOptions
    },

    getFilteredData () {
      if (this.stateBasedIn > 0) {
        return this.getLocationRegions({ id: this.stateBasedIn, params: {} }).then((response) => {
          this.locationSuburbsMeta = pick(response.data.meta, ['current_page', 'per_page', 'total', 'last_page'])
          if (this.basedInRegionOptions.length > 0) {
            this.basedInRegionOptions = []
          }
          this.pushRegionLocationItems(response, [])
        }).catch(() => {
        })
      }
    },

    handleGetLocationRegionsCallback (inputName) {
      return this.getLocationRegions({ id: this.stateBasedIn, params: { name: inputName } }).then((response) => {
        this.locationSuburbsMeta = pick(response.data.meta, ['current_page', 'per_page', 'total', 'last_page'])
        this.pushRegionLocationItems(response, [])
      }).catch(() => {
      })
    },

    handleGetNextLocationRegionsCallback ({ commit }, params = {}) {
      params = this.locationSuburbsMeta
      return this.getNextLocationRegions({ id: this.stateBasedIn, params }).then((response) => {
        this.locationSuburbsMeta = pick(response.data.meta, ['current_page', 'per_page', 'total', 'last_page'])
        this.pushRegionLocationItems(response, this.basedInRegionOptions)
      }).catch(() => {
      })
    }
  }
}
</script>
